import s from './lesson.card.module.scss'
import classNames from 'classnames'
import { useProfile } from '@shared/api/useProfile';
import { useMedia } from 'use-media';
import { TZDate } from '@date-fns/tz';
import { format } from 'date-fns'
import { ru } from 'date-fns/locale'
import { Tooltip } from 'react-tooltip'
import { IconCalendar, IconSchool, IconClock, IconUpload, IconX, IconMessageCircle, IconFileTypePdf, IconStar, IconRepeat } from '@tabler/icons-react'
import { Button } from '@shared/ui/button/button'
import { AppImage, DEFAULT_AVATAR_URL } from '@shared/ui/app-image'
import { canCancelLesson } from '@utils/lib/canCancelLesson';
import { useNavigate } from 'react-router-dom'
import { TRIAL_LESSON_PRICE } from '@shared/lib/trial'

const STATUSES = {
    active: { label: 'Активный', className: 'active' },
    expired: { label: 'Просрочен', className: 'expired' },
    cancelled: { label: 'Отменён', className: 'cancelled' },
    passed: { label: 'Прошёл', className: 'passed' },
}

export const LessonCard = ({ role = 'student', teacherImg, teacherName, participantName, status, subject, experience, date, createdAt, duration, price, isFree, isTrial, teacherSlug, receipt, onAttachReceipt, onCancel, onLeaveReview, onOpen }) => {

    const { data: profile } = useProfile()
    const isMobile = useMedia({ maxWidth: '768px' })

    const isTeacher = role === 'teacher'
    const navigate = useNavigate()
    const canOpenProfile = !isTeacher && !!teacherSlug
    const openProfile = (e) => { e?.stopPropagation(); if (canOpenProfile) navigate(`/teacher/${teacherSlug}`) }
    const currentStatus = STATUSES[status] ?? STATUSES.cancelled
    const isActive = status === 'active'
    const isPassed = status === 'passed'

    // Те же условия, что в AppointmentModal (см. canCancelLesson):
    // ученик — не позднее чем за день до начала или в течение 30 минут
    // после бронирования; преподаватель — всегда
    const canCancel = canCancelLesson({ status, startsAt: date, createdAt, timezone: profile?.timezone, isTeacher })

    const lessonDate = date ? new TZDate(date, profile?.timezone) : null
    const dateLabel = lessonDate ? format(lessonDate, 'd MMMM', { locale: ru }) : '—'
    const timeLabel = lessonDate ? format(lessonDate, 'HH:mm') : '—'

    const priceValue = isTrial ? TRIAL_LESSON_PRICE : price

    return (
        <div className={classNames(s.card, !isActive && s.muted, onOpen && s.clickableCard)} onClick={onOpen}>
            <div className={s.teacherWrapper}>
                <AppImage
                    className={classNames(s.teacherImg, canOpenProfile && s.clickable)}
                    onClick={openProfile}
                    alt='Аватарка'
                    src={teacherImg}
                    fallbackSrc={DEFAULT_AVATAR_URL}
                />
                <div className={s.teacherInfo}>
                    <div className={s.nameRow}>
                        <div className={classNames(s.teacherName, canOpenProfile && s.clickable)} onClick={openProfile}>{teacherName}</div>
                        <div className={classNames(s.badge, s[currentStatus.className])}>{currentStatus.label}</div>
                        {isTrial && <div className={classNames(s.badge, s.trialBadge)}>Пробное занятие</div>}
                        {participantName && <div className={classNames(s.badge, s.participantBadge)}>{participantName}</div>}
                    </div>
                    <div className={s.subText}>
                        <span>{subject}</span>
                        {experience ? <>
                            <span className={s.dot}>•</span>
                            <span>{experience}</span>
                        </> : <></>}
                    </div>
                </div>
            </div>

            <div className={classNames(s.minCard, s.dateCard)}>
                <IconCalendar className={s.icon} size={24}/>
                <div className={s.cardData}>
                    <span>{dateLabel}</span>
                    <span className={s.dot}>•</span>
                    <span>{timeLabel}</span>
                </div>
            </div>

            <div className={s.minCard}>
                <IconSchool className={s.icon} size={24}/>
                <div className={s.cardData}>{isFree ? 'Бесплатно' : (typeof priceValue === 'number' ? priceValue.toLocaleString('ru-RU') : '—') + ' ₽'}</div>
            </div>

            <div className={s.minCard}>
                <IconClock className={s.icon} size={24}/>
                <div className={s.cardData}>{duration ?? '—'} мин.</div>
            </div>

            <div className={s.actions} onClick={(e) => e.stopPropagation()}>
                {isTeacher ?
                    (isMobile ?
                    <>
                        {receipt ?
                            <a className={s.docTextLink} href={process.env.REACT_APP_STORAGE_URL + receipt} target='_blank' rel='noreferrer'>
                                <IconFileTypePdf size={20}/>
                                <span>Квитанция.pdf</span>
                            </a>
                            :
                            <label className={s.attachReceiptButton}>
                                Прикрепить чек
                                <input
                                    type='file'
                                    className={s.receiptInput}
                                    accept='.pdf,image/*'
                                    onChange={(e) => { const file = e.target.files?.[0]; if (file) onAttachReceipt?.(file); e.target.value = '' }}
                                />
                            </label>
                        }
                        {isActive
                            ? <Button className={s.cancelTextButton} onClick={onCancel} label='Отменить запись'/>
                            : <Button className={s.chatButton} to='/messenger' label='Чат с учеником'/>}
                    </>
                    :
                    <>
                        {receipt ?
                            <a className={s.downloadButton} href={process.env.REACT_APP_STORAGE_URL + receipt} target='_blank' rel='noreferrer' data-tooltip-id='lesson-card-tooltip' data-tooltip-content='Скачать чек'>
                                <IconFileTypePdf size={24}/>
                            </a>
                            :
                            <label className={s.downloadButton} data-tooltip-id='lesson-card-tooltip' data-tooltip-content='Прикрепить чек'>
                                <IconUpload size={24}/>
                                <input
                                    type='file'
                                    className={s.receiptInput}
                                    accept='.pdf,image/*'
                                    onChange={(e) => { const file = e.target.files?.[0]; if (file) onAttachReceipt?.(file); e.target.value = '' }}
                                />
                            </label>
                        }
                        {isActive ?
                            <Button className={s.cancelButton} onClick={onCancel} label={<IconX size={24}/>} data-tooltip-id='lesson-card-tooltip' data-tooltip-content='Отменить занятие'/>
                            :
                            <Button className={s.messageButton} to='/messenger' label={<IconMessageCircle size={24}/>} data-tooltip-id='lesson-card-tooltip' data-tooltip-content='Открыть чат'/>
                        }
                    </>
                    )
                    : isMobile ?
                    <>
                        <Button className={s.chatButton} to='/messenger' label='Чат'/>
                        {isActive
                            ? <Button className={s.cancelTextButton} onClick={onCancel} label='Отменить запись'/>
                            : <Button className={s.repeatTextButton} to={`/teacher/booking/${teacherSlug}`} label='Повторить'/>}
                    </>
                    :
                    <>
                        {isActive && canCancel ?
                            <Button className={s.cancelButton} onClick={onCancel} label={<IconX size={24}/>} data-tooltip-id='lesson-card-tooltip' data-tooltip-content='Отменить занятие'/>
                            :
                            <>
                                {isPassed ?
                                    <Button className={s.starButton} onClick={onLeaveReview} label={<IconStar size={24}/>} data-tooltip-id='lesson-card-tooltip' data-tooltip-content='Оставить отзыв'/>
                                    :
                                    <Button className={s.messageButton} to='/messenger' label={<IconMessageCircle size={24}/>} data-tooltip-id='lesson-card-tooltip' data-tooltip-content='Открыть чат'/>
                                }
                                <Button className={s.repeatButton} to={`/teacher/booking/${teacherSlug}`} label={<IconRepeat size={24}/>} data-tooltip-id='lesson-card-tooltip' data-tooltip-content='Записаться повторно'/>
                            </>
                        }
                    </>
                }
            </div>
            <Tooltip id='lesson-card-tooltip' />
        </div>
    )
}
